import React, { useState, useEffect, useMemo } from 'react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

// Two decks side-by-side. Student clicks the top card of a deck → it flips
// (3D rotateY) and slides across to the neighbouring deck. Empty stacks show
// a dashed placeholder so the layout stays stable while cards move.
const CardDeckElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onMovedChange = null,
  syncedMovedIds = null,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { content = {} } = element;
  const {
    cards = [],
    frontBg = '#F2EFE7',
    backBg = '#2A4D25',
    frontTextColor = '#1f2937',
    backTextColor = '#ffffff',
    fontFamily = 'Evolventa',
    fontSize = 18,
  } = content;

  const isControlled = Array.isArray(syncedMovedIds);
  const [localMoved, setLocalMoved] = useState([]);
  const movedIds = isControlled ? syncedMovedIds : localMoved;

  const cardsKey = cards.map((c) => c.id).join('|');
  useEffect(() => {
    if (!isControlled) setLocalMoved([]);
  }, [cardsKey, isControlled]);

  const { layout, topSourceId, topTargetId } = useMemo(() => {
    const sourceCards = cards.filter((c) => !movedIds.includes(c.id));
    const targetOrdered = [...movedIds]
      .map((id) => cards.find((c) => c.id === id))
      .filter(Boolean)
      .reverse();

    const SOURCE_CX = 27;
    const TARGET_CX = 73;
    const CY = 50;
    const STACK_OFFSET = 0.7;

    const map = {};
    sourceCards.forEach((c, i) => {
      map[c.id] = {
        cx: SOURCE_CX + i * STACK_OFFSET,
        cy: CY + i * STACK_OFFSET,
        z: 100 - i,
        side: 'source',
      };
    });
    targetOrdered.forEach((c, i) => {
      map[c.id] = {
        cx: TARGET_CX + i * STACK_OFFSET,
        cy: CY + i * STACK_OFFSET,
        z: 100 - i,
        side: 'target',
      };
    });

    return {
      layout: map,
      topSourceId: sourceCards[0]?.id || null,
      topTargetId: targetOrdered[0]?.id || null,
    };
  }, [cards, movedIds]);

  const moveCard = (cardId, direction) => {
    if (isEditor || disabled || !canInteract) return;
    const next = direction === 'toTarget'
      ? [...movedIds, cardId]
      : movedIds.filter((id) => id !== cardId);
    if (isControlled) {
      onMovedChange?.(next);
    } else {
      setLocalMoved(next);
      onMovedChange?.(next);
    }
  };

  const CARD_W = 34; // % of element width
  const CARD_H = 88; // % of element height

  const renderFace = (side, bg, textColor, isBack) => {
    const isImage = side?.type === 'image' && side?.imageUrl;
    return (
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        style={{
          backgroundColor: bg,
          borderRadius: 14,
          boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: isBack ? 'rotateY(180deg)' : 'none',
          padding: '8%',
        }}
      >
        {isImage ? (
          <img
            src={resolveMediaUrl(side.imageUrl)}
            alt=""
            className="max-w-full max-h-full object-contain"
            draggable={false}
          />
        ) : (
          <p
            className="text-center font-medium break-words"
            style={{
              color: textColor,
              fontFamily,
              fontSize: `${fontSize}px`,
              lineHeight: 1.25,
              margin: 0,
            }}
          >
            {side?.text || ''}
          </p>
        )}
      </div>
    );
  };

  const showPlaceholder = (side) => {
    const isSource = side === 'source';
    const hasCard = isSource ? topSourceId : topTargetId;
    if (hasCard) return null;
    const cx = isSource ? 27 : 73;
    return (
      <div
        className="absolute pointer-events-none"
        style={{
          left: `${cx}%`,
          top: '50%',
          width: `${CARD_W}%`,
          height: `${CARD_H}%`,
          transform: 'translate(-50%, -50%)',
          border: '2px dashed rgba(0,0,0,0.18)',
          borderRadius: 14,
          background: 'rgba(0,0,0,0.02)',
        }}
      />
    );
  };

  return (
    <div className="relative w-full h-full" style={{ perspective: '1400px' }}>
      {showPlaceholder('source')}
      {showPlaceholder('target')}

      {cards.map((card) => {
        const l = layout[card.id];
        if (!l) return null;
        const inSource = l.side === 'source';
        const isTop = card.id === (inSource ? topSourceId : topTargetId);
        const canClick = !isEditor && !disabled && canInteract && isTop;
        const rotateY = inSource ? 0 : 180;

        return (
          <div
            key={card.id}
            onClick={() => canClick && moveCard(card.id, inSource ? 'toTarget' : 'toSource')}
            className="absolute"
            style={{
              left: `${l.cx}%`,
              top: `${l.cy}%`,
              width: `${CARD_W}%`,
              height: `${CARD_H}%`,
              transform: 'translate(-50%, -50%)',
              transformStyle: 'preserve-3d',
              zIndex: l.z,
              cursor: canClick ? 'pointer' : 'default',
              transition: 'left 550ms cubic-bezier(0.4, 0, 0.2, 1), top 550ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <div
              className="w-full h-full relative"
              style={{
                transformStyle: 'preserve-3d',
                transform: `rotateY(${rotateY}deg)`,
                transition: 'transform 550ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {renderFace(card.front, frontBg, frontTextColor, false)}
              {renderFace(card.back, backBg, backTextColor, true)}
            </div>
          </div>
        );
      })}

      {isEditor && cards.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="px-4 py-2 rounded-lg bg-black/60 text-white text-xs font-medium shadow">
            {t('editor.cardDeck.editorEmpty', { defaultValue: 'Додай картки у панелі властивостей праворуч' })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardDeckElement;
